import React, { useEffect, useState } from "react";
import { Sparkles, Loader2, Trash2, Pencil, Plus, X, Check } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { adminApi, imgUrl, errMsg } from "../lib/api";
import { LibraryPicker } from "./MembersPanel";

const TYPES = [
  { id: "from_me", label: "From Me" },
  { id: "update", label: "My Updates" },
  { id: "poll", label: "Choose for Me" },
  { id: "for_you", label: "For You" },
];
const label = (id) => TYPES.find((x) => x.id === id)?.label || id;

function PostRow({ p, onSaved, onDelete }) {
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState({ title: p.title, text: p.text, images: p.images || [], options: p.options || [] });
  const save = async () => {
    try { await adminApi.patch(`/admin/posts/${p.id}`, f); toast.success("Saved"); setEdit(false); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="admin-card p-4 flex gap-4" data-testid={`post-row-${p.id}`}>
      <div className="flex gap-1.5 shrink-0">
        {(edit ? f.images : p.images || []).slice(0, 4).map((u, i) => (
          <span key={i} className="relative">
            <img src={imgUrl(u)} alt="" className="w-14 h-[72px] rounded-md object-cover" />
            {edit && <button onClick={() => setF({ ...f, images: f.images.filter((_, k) => k !== i) })} className="absolute -top-1.5 -right-1.5 bg-white rounded-full shadow p-0.5"><X className="w-3 h-3" /></button>}
          </span>
        ))}
        {edit && <LibraryPicker onPick={(u) => setF({ ...f, images: [...f.images, u] })} trigger={<button className="w-14 h-[72px] rounded-md border-2 border-dashed border-[#f2c3d3] grid place-items-center text-[#ee3a70]"><Plus className="w-4 h-4" /></button>} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-[11.5px]">
          <span className="admin-pill !py-0.5">{label(p.type)}</span>
          {p.ai_generated && <span className="text-[#7a7276] flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI</span>}
          {p.type === "poll" && <span className="text-[#7a7276]">{p.votes || 0} votes</span>}
          <span className="text-[#a79fa3]">{new Date(p.created_at).toLocaleString()}</span>
        </div>
        {edit ? (
          <div className="space-y-2 mt-2">
            <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-[#efe3e7] text-[14px]" data-testid="post-edit-title" />
            <textarea value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} rows={3} className="w-full px-3 py-2 rounded-lg border border-[#efe3e7] text-[14px]" data-testid="post-edit-text" />
            {p.type === "poll" && f.options.map((o, i) => (
              <input key={i} value={o} onChange={(e) => setF({ ...f, options: f.options.map((x, k) => (k === i ? e.target.value : x)) })} className="w-full h-8 px-3 rounded-lg border border-[#efe3e7] text-[13px]" />
            ))}
          </div>
        ) : (
          <>
            <p className="font-semibold text-[15px] mt-1">{p.title}</p>
            <p className="text-[13.5px] text-[#4a4246] line-clamp-2">{p.text}</p>
            {p.type === "poll" && <p className="text-[12.5px] text-[#7a7276] mt-1">Options: {p.options.join(" · ")}</p>}
          </>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        {edit ? (
          <><button className="icon-btn" onClick={save} data-testid="post-save"><Check className="w-4 h-4" /></button><button className="icon-btn" onClick={() => setEdit(false)}><X className="w-4 h-4" /></button></>
        ) : (
          <><button className="icon-btn" onClick={() => setEdit(true)} data-testid="post-edit"><Pencil className="w-4 h-4" /></button><button className="icon-btn danger" onClick={() => onDelete(p)} data-testid="post-delete"><Trash2 className="w-4 h-4" /></button></>
        )}
      </div>
    </div>
  );
}

export default function ContentPanel() {
  const [filter, setFilter] = useState("all");
  const [posts, setPosts] = useState(null);
  const [gen, setGen] = useState(null);
  const [members, setMembers] = useState([]);
  const [fyUser, setFyUser] = useState("");
  const [del, setDel] = useState(null);

  const load = async () => {
    try { const { data } = await adminApi.get("/admin/posts", { params: filter === "all" ? {} : { type: filter } }); setPosts(data); }
    catch (e) { toast.error(errMsg(e)); setPosts([]); }
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line
  useEffect(() => { adminApi.get("/admin/members").then((r) => setMembers(r.data)).catch(() => {}); }, []);

  const generate = async (type) => {
    if (type === "for_you" && !fyUser) return toast.error("Pick a member first");
    setGen(type);
    try { const { data } = await adminApi.post("/admin/generate", { type, user_id: type === "for_you" ? fyUser : undefined }); toast.success(`New ${label(type)} published: "${data.title}"`); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setGen(null); }
  };

  const confirmDel = async () => {
    const p = del; setDel(null);
    try { await adminApi.delete(`/admin/posts/${p.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <div className="admin-card p-5">
        <p className="font-semibold flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#ee3a70]" /> Generate with AI</p>
        <p className="text-[13px] text-[#7a7276] mt-1">AI writes Maya's text and picks photos from your member library. It's published instantly; edit or delete below.</p>
        <div className="flex flex-wrap gap-2 mt-4">
          {TYPES.filter((x) => x.id !== "for_you").map((x) => (
            <button key={x.id} onClick={() => generate(x.id)} disabled={!!gen} className="admin-btn h-10" data-testid={`gen-${x.id}`}>
              {gen === x.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {x.label}
            </button>
          ))}
          <div className="flex items-center gap-2 ml-0 md:ml-3">
            <Select value={fyUser} onValueChange={setFyUser}>
              <SelectTrigger className="w-[190px] h-10" data-testid="gen-foryou-member"><SelectValue placeholder="Choose member" /></SelectTrigger>
              <SelectContent>{members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
            </Select>
            <button onClick={() => generate("for_you")} disabled={!!gen} className="admin-ghost h-10" data-testid="gen-for_you">{gen === "for_you" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} For You</button>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mt-8 mb-4">
        <h2 className="text-[18px] font-semibold">Published content <span className="text-[#a79fa3] font-normal">({posts?.length || 0})</span></h2>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[180px] h-9" data-testid="content-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All types</SelectItem>{TYPES.map((x) => <SelectItem key={x.id} value={x.id}>{x.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {!posts ? <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#ee3a70]" /> : (
        <div className="space-y-3" data-testid="content-list">
          {posts.map((p) => <PostRow key={p.id} p={p} onSaved={load} onDelete={setDel} />)}
          {!posts.length && <p className="text-[14px] text-[#7a7276] text-center py-10">Nothing published yet.</p>}
        </div>
      )}

      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this post?</AlertDialogTitle><AlertDialogDescription>Members will no longer see it.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={confirmDel} className="bg-[#dc2626] hover:bg-[#b91c1c]" data-testid="post-delete-confirm">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
